import { EProjectFormPages } from "components/ProjectForm/EProjectFormPages"
import { EFileTypes } from "enums/fileTypeEnums"
import { enqueueSnackbar } from "notistack"
import { HistoryResponse, IDocHubDocument, IFile, IResponse, StatusButton } from "pages/Common/commonTypes"
import { api } from "../../api"
import type {
    IBudgetListResponse,
    IBudgetSummaryResponse,
    IEditChargerRowPayload,
    IEditMaterialRowPayload,
    IEditProjectCommonDetails,
    IInfraDetailsResponse,
    IManpowerItem,
    IOnboardingDetailsResponse,
    IOwnershipHistoryEntry,
    IParseMaterialsResponse,
    IPaymentDetailsPageData,
    IProjectCharger,
    IProjectListFilters,
    IProjectListResponse,
    IProjectMaterialDetailsAllResponse,
    IProjectMaterialDetailsResponse,
    IProjectProfileOverviewResponse,
    IProjectSalesDetailsResponse,
    IProjectStatsResponse,
    IProjectTabCount,
    IProjectTicket,
    IProjectWithChargersResponse,
    IPurchaseOrderDetail,
    ISiteAddressUpdatePayload,
    ISOW,
    ISOWTemplate,
    ISOWTemplateWithPages,
    ICreateDrFromUnclaimedPayload,
    ICreateDrFromUnclaimedResponse,
    ISyncTargetTicket,
    ISyncUnclaimedPayload,
    ISyncUnclaimedResponse,
    ITemplateUIRulesResponse,
    ITicketPageDataRequest,
    IUnclaimedMaterials,
    ParseChargersFileResponse,
    ProjectParentStatus,
} from "./ProjectTypes"

// Re-export all types from ProjectTypes for backward compatibility
export * from "./ProjectTypes"

const MODULE_URL = "projects"

// Additional types not in ProjectTypes.ts
export interface IEditChargersPayload {
    project_id: string
    is_admin: 0 | 1
    rows: IEditChargerRowPayload[]
}

export interface IEditMaterialsPayload {
    project_id: string
    is_admin: 0 | 1
    rows: IEditMaterialRowPayload[]
}

export interface IMaterialEditResponse {
    status: string
    project_id: number
    data: any
}

export interface IDocHubFilters {
    project_id: string
    module_id?: number
    submodule_id?: number
    doc_type?: EFileTypes
    uploaded_by?: number
}

const projectService = api.injectEndpoints({
    endpoints: build => ({
        // Queries
        getFormStepDetail: build.query<any, { page: number; project_id: string }>({
            query: params => ({
                url: `${MODULE_URL}/get_project_details`,
                params: params,
            }),
            providesTags: (result, error, args) => [{ type: "Projects", id: args.project_id }],
        }),
        getProjectChargerTemplate: build.query<string, void>({
            query: () => ({
                url: `${MODULE_URL}/charger_template`,
            }),
            transformResponse: (response: [{ url: string }, number]) => {
                return response[0].url
            },
        }),
        // Project List Endpoints
        getProjectTabs: build.query<IProjectTabCount[], IProjectListFilters>({
            query: (filters = {}) => ({
                url: `${MODULE_URL}/list/total`,
                params: filters,
                useV3: true,
            }),
            transformResponse: (res: { message: IProjectTabCount[] }) => res.message,
            providesTags: ["Projects"],
        }),

        getProjectList: build.query<IProjectListResponse, { parent_status: ProjectParentStatus } & IProjectListFilters>(
            {
                query: ({ parent_status, ...filters }) => ({
                    url: `${MODULE_URL}/list/${parent_status}`,
                    params: filters,
                    useV3: true,
                }),
                providesTags: ["Projects"],
            }
        ),

        downloadProjects: build.query<IFile, { parent_status: ProjectParentStatus } & IProjectListFilters>({
            query: filters => ({
                url: `${MODULE_URL}/list/download`,
                params: filters,
                useV3: true,
            }),
            providesTags: ["Projects"],
        }),

        // Project Profile Endpoints
        getProjectStats: build.query<IProjectStatsResponse, string>({
            query: projectId => ({
                url: `${MODULE_URL}/overall_progress`,
                params: { project_id: projectId },
                useV3: true,
            }),
            transformResponse: (response: { data: IProjectStatsResponse; success: boolean; project_id: number }) => {
                return response.data
            },
            providesTags: ["Projects"],
        }),
        getProjectProfileOverview: build.query<IProjectProfileOverviewResponse, string>({
            query: projectId => ({
                url: `${MODULE_URL}/project_profile_overview`,
                params: { project_id: projectId },
                useV3: true,
            }),
            transformResponse: (response: {
                data: IProjectProfileOverviewResponse
                success: boolean
                project_id: number
            }) => {
                return response.data
            },
            providesTags: ["Projects"],
        }),
        getProjectMaterialDetails: build.query<IProjectMaterialDetailsResponse, string>({
            query: projectId => ({
                url: `${MODULE_URL}/material_details`,
                params: { project_id: projectId },
                useV3: true,
            }),
            transformResponse: (response: { data: IProjectMaterialDetailsResponse; success: boolean }) => {
                return response.data
            },
            providesTags: ["Projects"],
        }),
        // Chargers and materials as one list — additive, /material_details above is unchanged.
        getProjectMaterialDetailsAll: build.query<IProjectMaterialDetailsAllResponse, string>({
            query: projectId => ({
                url: `${MODULE_URL}/material_details_all`,
                params: { project_id: projectId },
                useV3: true,
            }),
            transformResponse: (response: { data: IProjectMaterialDetailsAllResponse; success: boolean }) => {
                return response.data
            },
            providesTags: ["Projects"],
        }),
        // Material sync — attach unclaimed chargers/items to a DR ticket still Awaiting allocation.
        getUnclaimedMaterials: build.query<IUnclaimedMaterials, string | number>({
            query: projectId => ({
                url: `${MODULE_URL}/material_details`,
                params: { project_id: projectId, unclaimed: true },
                useV3: true,
            }),
            transformResponse: (response: { data: IUnclaimedMaterials }) => response.data,
            // Same tag as the normal material list, so a sync refreshes both.
            providesTags: ["Projects"],
        }),
        getSyncTargetTickets: build.query<ISyncTargetTicket[], string | number>({
            query: projectId => ({
                url: `dispatch_request/open_tickets`,
                params: { project_id: projectId },
                useV3: true,
            }),
            transformResponse: (response: { data: { tickets: ISyncTargetTicket[] } }) => response.data?.tickets ?? [],
        }),
        // All or nothing: a 400 writes nothing. Errors are shown inside the modal, not as a toast.
        syncUnclaimedMaterials: build.mutation<ISyncUnclaimedResponse, ISyncUnclaimedPayload>({
            query: body => ({
                url: `dispatch_request/sync_unclaimed_plps`,
                method: "POST",
                body,
                useV3: true,
            }),
            transformResponse: (response: { data: ISyncUnclaimedResponse }) => response.data,
            invalidatesTags: ["Projects"],
        }),
        createDrFromUnclaimed: build.mutation<ICreateDrFromUnclaimedResponse, ICreateDrFromUnclaimedPayload>({
            query: body => ({
                url: `dispatch_request/create_from_unclaimed`,
                method: "POST",
                body,
                useV3: true,
            }),
            transformResponse: (response: { data: ICreateDrFromUnclaimedResponse }) => response.data,
            invalidatesTags: ["Projects", "DispatchRequest"],
        }),
        getProjectSalesDetails: build.query<IProjectSalesDetailsResponse, string>({
            query: projectId => ({
                url: `${MODULE_URL}/sales_details`,
                params: {
                    project_id: projectId,
                },
                useV3: true,
            }),
            transformResponse: (response: { data: IProjectSalesDetailsResponse; success: boolean }) => {
                return response.data
            },
            providesTags: ["Projects"],
        }),
        getProjectInfraDetails: build.query<IInfraDetailsResponse, string>({
            query: projectId => ({
                url: `${MODULE_URL}/infra_details`,
                params: {
                    project_id: projectId,
                },
                useV3: true,
            }),
            transformResponse: (response: { data: IInfraDetailsResponse; success: boolean }) => {
                return response.data
            },
            providesTags: ["Projects"],
        }),
        getProjectOnboardingDetails: build.query<IOnboardingDetailsResponse, string>({
            query: projectId => ({
                url: `${MODULE_URL}/onboarding_details`,
                params: {
                    project_id: projectId,
                },
                useV3: true,
            }),
            transformResponse: (response: {
                data: IOnboardingDetailsResponse & { franchise_deatils?: any }
                assigned_to_details?: { id: number; name: string } | null
                success: boolean
            }) => {
                const { franchise_deatils, ...rest } = response.data as any
                return {
                    ...rest,
                    franchise_details: franchise_deatils ?? null,
                    assigned_to_details: response.assigned_to_details ?? null,
                }
            },
            providesTags: ["Projects"],
        }),
        getAllSOWs: build.query<ISOW[], void>({
            query: () => ({
                url: `${MODULE_URL}/sow-templates/sows`,
                useV3: true,
            }),
            transformResponse: (response: { data: ISOW[]; success: boolean }) => {
                return response.data
            },
            providesTags: ["Projects"],
        }),
        getSOWTemplates: build.query<
            ISOWTemplate[],
            void | {
                project_id?: string
                sow_id?: string
                module_id?: number
                submodule_id?: string
                status_id?: string
                search_key?: string
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/sow-templates`,
                params: params || {},
                useV3: true,
            }),
            transformResponse: (response: { data: ISOWTemplate[]; success: boolean }) => {
                return response.data
            },
            providesTags: ["Projects"],
        }),

        getSurveysForProject: build.query<
            {
                id: number
                name: string
            }[],
            void
        >({
            query: () => ({
                url: `${MODULE_URL}/link_solo_survey`,
                useV3: true,
            }),
            transformResponse: (response: IResponse<{ id: number; name: string }[]>) => {
                return response.data
            },
        }),

        getProjectFormDetailsByStep: build.query<
            any,
            {
                page_title?: EProjectFormPages
                project_id: number
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/project_form`,
                params: params,
                useV3: true,
            }),
        }),
        editSOWTemplate: build.mutation<
            { message: string; success: boolean },
            { template_id: number; template_name?: string; description?: string; is_active?: 0 | 1 }
        >({
            query: body => ({
                url: `${MODULE_URL}/sow-templates`,
                method: "PUT",
                body: body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.success) {
                        enqueueSnackbar(data.message, {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),

        getProjectsWithDispatchedChargers: build.query<IProjectWithChargersResponse[], void>({
            query: () => ({
                url: `get_dispatched_charger_projects`,
                useV3: true,
            }),
            transformResponse: (response: {
                data: IProjectWithChargersResponse[]
                status: string
                have_more: boolean
                page: number
                page_size: number
            }) => {
                return response.data?.reverse()
            },
        }),

        getDispatchedChargerProjectsPaginated: build.query<
            {
                status: string
                data: IProjectWithChargersResponse[]
                have_more: boolean
                page: number
                page_size: number
            },
            { page: number; page_size: number; search?: string }
        >({
            query: params => ({
                url: `get_dispatched_charger_projects`,
                useV3: true,
                params,
            }),
        }),

        getFormChargers: build.query<IProjectCharger[], number>({
            query: project_id => ({
                url: `/get_project_chargers`,
                params: {
                    project_id,
                },
                useV3: true,
            }),
            transformResponse: (response: { data: IProjectCharger[]; success: boolean }) => {
                return response.data
            },
            providesTags: [{ type: "Projects", id: EProjectFormPages.MATERIAL_DETAILS }],
        }),

        getTemplatePages: build.query<ISOWTemplateWithPages, number>({
            query: template_id => ({
                url: `${MODULE_URL}/sow-templates/pages`,
                params: {
                    template_id,
                },
                useV3: true,
            }),
            transformResponse: (response: { data: ISOWTemplateWithPages; success: boolean }) => {
                return response.data
            },
            providesTags: ["Projects"],
        }),

        getTemplateUIRules: build.query<ITemplateUIRulesResponse, number>({
            query: template_id => ({
                url: `${MODULE_URL}/template_ui_rules`,
                params: {
                    template_id,
                },
                useV3: true,
            }),
            transformResponse: (response: { data: ITemplateUIRulesResponse; success: boolean }) => {
                return response.data
            },
            providesTags: (_, __, template_id) => [{ type: "Projects", id: `ui_rules_${template_id}` }],
        }),
        getProjectButtonDropdown: build.query<StatusButton[], string>({
            query: projectId => ({
                url: `${MODULE_URL}/${projectId}/button_dropdown`,
                useV3: true,
            }),
            transformResponse: (res: { buttons: StatusButton[] }) => res.buttons,
            providesTags: ["Projects"],
        }),
        getProjectHistory: build.query<HistoryResponse, { project_id: string }>({
            query: ({ project_id }) => ({
                url: `${MODULE_URL}/history?project_id=${project_id}`,
                useV3: true,
            }),
            providesTags: (result, error, arg) => ["Projects"],
        }),

        getOwnershipHistory: build.query<{ data: IOwnershipHistoryEntry[] }, { site_id: number }>({
            query: ({ site_id }) => ({
                url: `site/ownership_history`,
                params: { site_id },
                useV3: true,
            }),
            providesTags: ["Projects"],
        }),

        getEcomOrderIds: build.query<{ id: string; title: string }[], void>({
            query: () => ({
                url: `${MODULE_URL}/ecom_order_ids`,
            }),
            transformResponse: (response: any) =>
                (response?.data?.message ?? []).map((item: any) => ({ id: item, title: String(item) })),
        }),
        // Mutations
        addProjectTicket: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/tickets`,
                method: "POST",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects"],
        }),
        updateTicketPageData: build.mutation<
            { page_title: EProjectFormPages; result: any; status: string },
            ITicketPageDataRequest
        >({
            query: body => ({
                url: `${MODULE_URL}/ticket_pages_patch`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: (_, __, args) => [{ type: "Projects", id: args.page_title }], //TODO: Add another tag when this is called at the last page
        }),
        updateProjectTicket: build.mutation<any, any>({
            query: body => ({
                url: `${MODULE_URL}/tickets`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Projects"],
        }),
        addProjectTicketChargers: build.mutation({
            query: body => ({
                url: `add_charger`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["Projects"],
        }),
        updateProjectTicketChargers: build.mutation<{ message: string }, FormData>({
            query: body => ({
                url: `update_charger`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Projects"],
        }),
        parseMaterialsFile: build.mutation<IParseMaterialsResponse, FormData>({
            query: body => ({
                url: `${MODULE_URL}/parse_materials`,
                method: "POST",
                useV3: true,
                body,
            }),
            transformResponse: (res: IResponse<IParseMaterialsResponse>) => res.data,
        }),
        parseChargersFile: build.mutation<ParseChargersFileResponse, FormData>({
            query: body => ({
                url: `${MODULE_URL}/parse_chargers`,
                method: "POST",
                useV3: true,
                body,
            }),
            transformResponse: (res: IResponse<ParseChargersFileResponse>) => res.data,
        }),
        linkProjectToSurvey: build.mutation<IResponse<string>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/link_solo_survey`,
                method: "PUT",
                body,
                useV3: true,
            }),
        }),
        reconcileTemplateChanges: build.mutation<
            any,
            {
                old_template_id: number
                new_template_id: number
                project_id: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/reconcile_template_change`,
                useV3: true,
                body: body,
                method: "POST",
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    enqueueSnackbar(error?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
        }),
        templateTriggers: build.mutation<
            any,
            {
                template_id: number
                project_id: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/template_triggers`,
                method: "POST",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects", "EvApplication"],
        }),
        updateSiteAddress: build.mutation<{ message: string; success: boolean }, ISiteAddressUpdatePayload>({
            query: body => ({
                url: `${MODULE_URL}/project_profile/site_address/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.success) {
                        enqueueSnackbar(data.message || "Site address updated successfully", {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        updateManpowerAddress: build.mutation<
            { message: string; success: boolean },
            {
                project_id: string
                manpower_obj: IManpowerItem[]
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/manpower/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects"],
        }),
        updateProjectCommonDetails: build.mutation<{ message: string; success: boolean }, IEditProjectCommonDetails>({
            query: body => ({
                url: `${MODULE_URL}/project_profile/common/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects"],
        }),
        updateEVApplication: build.mutation<
            { message: string; success: boolean },
            {
                project_id: string
                is_eva_required?: number
                ev_connection_owned_by?: string
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/infra/ev_application/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects"],
        }),
        updateCommissioning: build.mutation<
            { message: string; success: boolean },
            {
                project_id: string
                cms_url: string
                port_number: string
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/infra/commissioning/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects"],
        }),
        updateInfraOtherDetails: build.mutation<
            { message: string; success: boolean },
            {
                project_id: string
                infra_remarks?: string
                user_id?: number
                ops_ticket_id?: number
                files?: { file_type: string; file_name: string[] }[]
                removed_doc_ids?: number[]
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/infra/other_details/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects", "Installation"],
        }),
        updateInfraScopeOfWork: build.mutation<
            { message: string; success: boolean },
            {
                project_id: string
                sow_group_ids: number[]
                ops_ticket_id?: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/infra/scope_of_work/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects"],
        }),
        // Material/Charger Edit APIs (v3)
        editChargers: build.mutation<IMaterialEditResponse, IEditChargersPayload>({
            query: body => ({
                url: `${MODULE_URL}/project_profile/materials/chargers/edit`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.status === "SUCCESS") {
                        enqueueSnackbar("Charger details updated successfully", {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Failed to update charger details", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        editMaterials: build.mutation<IMaterialEditResponse, IEditMaterialsPayload>({
            query: body => ({
                url: `${MODULE_URL}/project_profile/materials/items/edit`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.status === "SUCCESS") {
                        enqueueSnackbar("Material details updated successfully", {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Failed to update material details", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        // Station Details Update API
        updateStationDetails: build.mutation<
            { message: string; success: boolean },
            {
                project_id: number
                site_id: number
                name?: string
                access_type?: string
                is_open_24_hours?: number
                open_time?: string
                close_time?: string
                is_cobranded?: number
                cobrand_id?: number
                amenities?: number[]
                csms_station_id?: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/onboarding/station_details/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.success) {
                        enqueueSnackbar(data.message || "Station details updated successfully", {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Failed to update station details", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        // CPO Details Update API
        updateCPODetails: build.mutation<
            { message: string; success: boolean },
            {
                project_id: number
                site_id: number
                cpo_id: number
                csms_partner: string
                deal_amount?: number
                deal_amount_esc?: number
                license_fee_ac: number
                license_fee_dc: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/onboarding/cpo_details/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.success) {
                        enqueueSnackbar(data.message || "CPO details updated successfully", {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Failed to update CPO details", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        // Onboarding Host Details Update API
        updateOnboardingHostDetails: build.mutation<
            { message: string; success: boolean },
            {
                host_id: number
                site_id: number
                ticket_id: number
                host_rent?: number
                rent_esc?: number
                rent_esc_amt?: number
                rent_esc_duration?: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/onboarding/host_details/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.success) {
                        enqueueSnackbar(data.message || "Host details updated successfully", {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Failed to update host details", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        // Onboarding Land Details Update API
        updateOnboardingLandDetails: build.mutation<
            { message: string; success: boolean },
            {
                project_id: number
                site_id: number
                account_id?: number
                property_type?: string
                connection_type?: string
                sanctioned_load?: number
                land_size?: number
                dg_connection_used?: number
                dg_unit_rate?: number
                parking_slots?: number
                parking_type?: string
                vehicle_movement?: string
                deal_type?: string
                deal_amount?: number
                profit_share_percent?: number
                incremental_percent?: number
                incremental_duration?: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/onboarding/land_details/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.success) {
                        enqueueSnackbar(data.message || "Land details updated successfully", {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Failed to update land details", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        // Onboarding Site Address Update API
        updateOnboardingSiteAddress: build.mutation<
            { message: string; success: boolean },
            {
                project_id: number
                site_id: number
                name?: string | null
                csms_station_id?: number | null
                address?: string | null
                landmark?: string | null
                country_id?: number | null
                state_id?: number | null
                city_id?: number | null
                pincode?: number | null
                latitude?: number | null
                longitude?: number | null
                access_type?: string | null
                is_open_24_hours?: number | null
                open_time?: string | null
                close_time?: string | null
                is_cobranded?: number | null
                cobrand_id?: number | null
                amenities?: number[] | null
                location_type?: string | null
                location_subtype?: string | null
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/onboarding/site_address/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.success) {
                        enqueueSnackbar(data.message || "Site address updated successfully", {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Failed to update site address", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        // Onboarding Charger Details Update API
        updateOnboardingChargerDetails: build.mutation<
            { status: string; project_id: number; data: { site_id: number; updated: number } },
            {
                project_id: number
                site_id: number
                chargers: Array<{
                    charger_id: number
                    name?: string
                    operational_power_rating?: number | null
                    cpo_cost?: number | null
                    host_rent?: number | null
                    host_profit_amt?: number | null
                    host_profit_type?: number | null
                    host_profit?: number | null
                }>
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/project_profile/onboarding/charger_details/update`,
                method: "PUT",
                body,
                useV3: true,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    if (data.status === "SUCCESS") {
                        enqueueSnackbar("Charger details updated successfully", {
                            variant: "success",
                        })
                    }
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.message || "Failed to update charger details", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        // Project Tickets List API (v3)
        getProjectTickets: build.query<
            {
                tickets: IProjectTicket[]
                msrd_enabled: boolean
                total: number
                project_id: number
            },
            {
                projectId: number | string
            }
        >({
            query: ({ projectId }) => ({
                url: `${MODULE_URL}/${projectId}/tickets`,
                useV3: true,
            }),
            providesTags: ["Projects", "Installation"],
        }),
        // DocHub API (v3)
        getProjectDocHub: build.query<IDocHubDocument[], IDocHubFilters>({
            query: params => ({
                url: `${MODULE_URL}/dochub`,
                params,
                useV3: true,
            }),
            transformResponse: (response: { data: IDocHubDocument[]; success: boolean }) => {
                return response.data || []
            },
            providesTags: ["Projects"],
        }),
        getPurchaseOrderDetails: build.query<IPurchaseOrderDetail | null, { poId: string; projectId: string }>({
            query: ({ poId, projectId }) => ({
                url: `${MODULE_URL}/po/details`,
                params: { po_id: poId, project_id: projectId },
                useV3: true,
            }),
            transformResponse: (response: any, _, arg) => {
                const data = response?.data?.data
                if (Array.isArray(data) && data.length > 0) return data?.find(po => po.po_id === Number(arg.poId))
                return null
            },
            providesTags: ["Projects"],
        }),
        delinkPurchaseOrder: build.mutation<{ msg: string }, { project_id: number; po_id: number }>({
            query: body => ({
                url: `${MODULE_URL}/po/delink`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res?.data?.msg || "PO delinked successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        linkPurchaseOrder: build.mutation<
            { msg: string },
            {
                project_id: number
                po_id: number
                sub_ticket_id?: number
                module_id?: number
                submodule_id?: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/po/link`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res?.data?.msg || "PO linked successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        uploadPoDocument: build.mutation<any, FormData>({
            query: body => ({
                url: `${MODULE_URL}/po/upload_document`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res?.data?.msg || "Document uploaded successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        uploadPaymentEvidence: build.mutation<any, FormData>({
            query: body => ({
                url: `${MODULE_URL}/finance/upload-evidence`,
                method: "POST",
                useV3: true,
                body,
            }),
        }),
        editPurchaseOrder: build.mutation<{ msg: string; po_id: number }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/po/edit`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res?.data?.msg || "Purchase Order updated successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        getProjectInvoices: build.query<{ invoices: any[] }, number>({
            query: project_id => ({
                url: `${MODULE_URL}/${project_id}/tax-invoices`,
                method: "GET",
                useV3: true,
            }),
            providesTags: [{ type: "Projects", id: "invoices" }],
        }),
        mapSiteToProject: build.mutation<
            { message: string },
            {
                site_id: number
                project_id: number
                site_type: "destination" | "source"
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/site_mapping`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const res = await queryFulfilled
                    enqueueSnackbar(res?.data?.message || "Site mapped to project successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        deletePaymentTerms: build.mutation<any, { project_id: number; payment_term_id: number }>({
            query: ({ project_id, payment_term_id }) => ({
                url: `${MODULE_URL}/payment_terms/delete`,
                method: "DELETE",
                useV3: true,
                body: { project_id, payment_term_id },
            }),
            invalidatesTags: ["Projects", "Finance"],
        }),
        updatePaymentTerms: build.mutation<any, Omit<IPaymentDetailsPageData, "page_title">>({
            query: body => ({
                url: `${MODULE_URL}/finance/upsert`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                    enqueueSnackbar("Payment terms updated successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects", "Finance"],
        }),
        updateAtpm: build.mutation<
            { message: string; atpm_id: number },
            {
                account_id: number
                project_id: number
                account_type_id: number
                billing_address_id: number
                contact_id: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/atpm`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data.message || "Account mapped successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),

        linkBillingAddressToProjectAccount: build.mutation<
            any,
            {
                billing_address_id: number
                project_id: number
                account_id: number
                account_type_id: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/link_billing_address`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data.message || "Billing address linked successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),

        linkContactToProjectAccount: build.mutation<
            any,
            {
                contact_id: number
                project_id: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/link_contact`,
                method: "POST",
                useV3: true,
                body: {
                    ...body,
                    is_primary: 0,
                },
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data.message || "Contact linked successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        markTicketAsRead: build.mutation({
            query: projectId => ({
                url: `${MODULE_URL}/mark_read`,
                method: "POST",
                useV3: true,
                body: {
                    project_id: projectId,
                },
            }),
            invalidatesTags: ["Projects"],
        }),
        dropProjectTicket: build.mutation<
            any,
            {
                project_id: number
                reason_id: number
            }
        >({
            query: ({ project_id, reason_id }) => ({
                url: `${MODULE_URL}/drop`,
                method: "POST",
                useV3: true,
                body: {
                    project_id,
                    reason_id,
                },
            }),
            invalidatesTags: ["Projects"],
        }),
        evaProjectToggle: build.mutation<
            any,
            {
                is_ev_application_req: number
                project_id: string
            }
        >({
            query: ({ project_id, is_ev_application_req }) => ({
                url: `${MODULE_URL}/ev_application/toggle`,
                method: "PUT",
                useV3: true,
                body: {
                    project_id,
                    is_ev_application_req,
                },
            }),
            invalidatesTags: ["Projects"],
        }),
        editValidityInfo: build.mutation<
            any,
            { project_id: number; account_type_id: number; account: number; validity_from: string; validity_to: string }
        >({
            query: body => ({
                url: `${MODULE_URL}/atpm/validity`,
                method: "PUT",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data.message || "Validity info updated successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),

        transferOwnershipToFranchise: build.mutation<
            { message: string },
            {
                franchise_id: number
                site_id: number
                user_id: number
                project_id: number
                mapping_date: string
                billing_address_id: number
                contact_id: number
                contact_type_id: number
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/transfer_ownership`,
                method: "POST",
                useV3: true,
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data.message || "Ownership transferred to Franchise successfully", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        setMsrdDeliveryDate: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/msrd/set_delivery_date`,
                useV3: true,
                method: "PUT",
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message || "Success", {
                        variant: "success",
                    })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", {
                        variant: "error",
                    })
                }
            },
            invalidatesTags: ["Projects"],
        }),
        // Read-only preview of whether an MSRD merges into an open DR / Logistics ticket.
        getMsrdLogisticsMergeStatus: build.query<
            {
                will_merge: boolean
                ticket_type: "Dispatch_request" | "Logistics" | null
                dr_ticket_id: number | null
                logistics_ticket_id: number | null
                message: string
            },
            { project_id: number; user_id: number }
        >({
            query: params => ({
                url: `${MODULE_URL}/msrd/logistics_merge_status`,
                useV3: true,
                params,
            }),
            keepUnusedDataFor: 0,
        }),
        // ==================== Budget APIs ====================
        getProjectBudgetSummary: build.query<
            IBudgetSummaryResponse,
            { project_id: number | string; page?: number; per_page?: number }
        >({
            query: params => ({
                url: `${MODULE_URL}/budget/summary`,
                params,
                useV3: true,
            }),
            providesTags: ["Projects"],
        }),

        getProjectBudgets: build.query<IBudgetListResponse, number | string>({
            query: project_id => ({
                url: `${MODULE_URL}/budget`,
                params: { project_id },
                useV3: true,
            }),
            providesTags: ["Projects"],
        }),

        addProjectBudgets: build.mutation<
            { message: string; project_id: number | string; total_budget: number; inserted: number },
            { project_id: number | string; budgets: { category: string; amount: number; remarks: string | null }[] }
        >({
            query: body => ({
                url: `${MODULE_URL}/budget`,
                method: "POST",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects"],
        }),

        updateProjectBudgets: build.mutation<
            { message: string; total_budget: number },
            {
                project_id: number | string
                budgets: { budget_id: number; category: string; amount: number; remarks: string | null }[]
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/budget`,
                method: "PUT",
                body,
                useV3: true,
            }),
            invalidatesTags: ["Projects"],
        }),

        deleteProjectBudgets: build.mutation<
            { message: string; deleted_ids: string[]; total_budget: number },
            { budget_ids: string }
        >({
            query: ({ budget_ids }) => ({
                url: `${MODULE_URL}/budget`,
                params: { budget_ids },
                method: "DELETE",
                useV3: true,
            }),
            invalidatesTags: ["Projects"],
        }),

        editInfraChargerDetails: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/project_profile/infra/charger_details/update`,
                useV3: true,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Projects", "Installation"],
        }),
    }),
})

export const {
    // Queries
    useGetFormStepDetailQuery,
    useGetProjectChargerTemplateQuery,
    useGetSOWTemplatesQuery,
    useGetTemplatePagesQuery,
    useLazyGetTemplatePagesQuery,
    useGetAllSOWsQuery,
    useGetFormChargersQuery,
    useLazyGetFormChargersQuery,
    useGetSurveysForProjectQuery,
    useGetProjectsWithDispatchedChargersQuery,
    useLazyGetDispatchedChargerProjectsPaginatedQuery,
    useLazyGetProjectFormDetailsByStepQuery,
    useGetTemplateUIRulesQuery,
    useLazyGetTemplateUIRulesQuery,
    useGetProjectInvoicesQuery,
    //Project List
    useGetProjectTabsQuery,
    useGetProjectListQuery,
    useLazyDownloadProjectsQuery,
    useGetEcomOrderIdsQuery,

    // Project profile
    useGetProjectButtonDropdownQuery,
    useGetProjectProfileOverviewQuery,
    useGetProjectMaterialDetailsQuery,
    useGetProjectMaterialDetailsAllQuery,
    useGetUnclaimedMaterialsQuery,
    useGetSyncTargetTicketsQuery,
    useSyncUnclaimedMaterialsMutation,
    useCreateDrFromUnclaimedMutation,
    useGetProjectStatsQuery,
    useGetProjectSalesDetailsQuery,
    useGetProjectInfraDetailsQuery,
    useGetProjectOnboardingDetailsQuery,
    useEditValidityInfoMutation,
    useGetProjectHistoryQuery,
    useEditInfraChargerDetailsMutation,
    useTransferOwnershipToFranchiseMutation,
    useGetOwnershipHistoryQuery,
    // Mutations
    useAddProjectTicketMutation,
    useAddProjectTicketChargersMutation,
    useUpdateProjectTicketChargersMutation,
    useEditSOWTemplateMutation,
    useParseMaterialsFileMutation,
    useParseChargersFileMutation,
    useUpdateTicketPageDataMutation,
    useLinkProjectToSurveyMutation,
    useReconcileTemplateChangesMutation,
    useTemplateTriggersMutation,
    useUpdateSiteAddressMutation,
    useUpdateManpowerAddressMutation,
    useUpdateProjectCommonDetailsMutation,
    useUpdateEVApplicationMutation,
    useUpdateCommissioningMutation,
    useUpdateInfraOtherDetailsMutation,
    useUpdateInfraScopeOfWorkMutation,
    useEditChargersMutation,
    useEditMaterialsMutation,
    useGetProjectDocHubQuery,
    useGetPurchaseOrderDetailsQuery,
    useLazyGetPurchaseOrderDetailsQuery,
    useDelinkPurchaseOrderMutation,
    useLinkPurchaseOrderMutation,
    useEditPurchaseOrderMutation,
    useUpdateStationDetailsMutation,
    useUpdateCPODetailsMutation,
    useUpdateOnboardingSiteAddressMutation,
    useUpdateOnboardingLandDetailsMutation,
    useUpdateOnboardingHostDetailsMutation,
    useUpdateOnboardingChargerDetailsMutation,
    useUploadPoDocumentMutation,
    useUploadPaymentEvidenceMutation,
    useGetProjectTicketsQuery,
    useMapSiteToProjectMutation,
    useDeletePaymentTermsMutation,
    useUpdateAtpmMutation,
    useLinkBillingAddressToProjectAccountMutation,
    useLinkContactToProjectAccountMutation,
    useMarkTicketAsReadMutation,
    useUpdatePaymentTermsMutation,
    useDropProjectTicketMutation,
    useEvaProjectToggleMutation,
    useSetMsrdDeliveryDateMutation,
    useGetMsrdLogisticsMergeStatusQuery,
    // Budget
    useGetProjectBudgetSummaryQuery,
    useGetProjectBudgetsQuery,
    useAddProjectBudgetsMutation,
    useUpdateProjectBudgetsMutation,
    useDeleteProjectBudgetsMutation,
} = projectService
